import {
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { DateRange, DateVariant } from "components/base/DateComponent";
import { CustomTooltip, Section } from "components/disw";
import Badge from "components/disw/Badge";
import { FormikProps } from "formik";
import {
  NotificationTriggerInput,
  ServiceProductStatus
} from "model/schema/globalTypes";
import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { createRoute } from "utils/url";
import DetailedTransactionQuotaBar from "./DetailedTransationQuotaBar";
import helpIcon from "./icons/help.svg";
import PeriodsWithQuota from "./PeriodsWithQuota";
import ServiceProductNotificationActions from "./ServiceProductNotificationActions";
import ServiceProductNotificationEdit from "./ServiceProductNotificationEdit";
import ServiceProductNotificationStatus from "./ServiceProductNotificationStatus";
import {
  hasNotificationProperties,
  ServiceProduct,
  ServiceProductWithSolution
} from "./types";

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #00000026",
    borderRadius: 3,
    padding: theme.spacing(2),
    gap: theme.spacing(3),
    boxShadow: "0px 1px 2px 0px #0000004D",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    "& > :last-child": {
      paddingBottom: "0px !important"
    }
  },
  label: {
    fontFamily: "Segoe UI",
    fontWeight: 700,
    fontSize: "14.4px",
    lineHeight: "19px",
    letterSpacing: 0,
    color: "#1E1E1E",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    marginBottom: 0
  },
  labelDetailed: {
    paddingLeft: 16
  },
  propertyGrid: {
    minWidth: 112,
    maxWidth: 112,
    width: 112,
    flexBasis: 112,
    flexShrink: 0,
    flexGrow: 0,
    paddingRight: 8
  },
  property: {
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#1E1E1E"
  },
  value: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#464646",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%"
  },
  helpIcon: {
    display: "inline-flex"
  },
  valueWithIcon: {
    display: "flex",
    alignItems: "center",
    gap: 4
  },
  helpIconImg: {
    display: "inline-block",
    verticalAlign: "middle",
    margin: 0,
    padding: 0
  },
  cardContent: {
    padding: 0
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
    padding: 0,
    gap: theme.spacing(1),
    marginTop: "-16px"
  },
  flex: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  budgetStatusText: {
    fontFamily: "Segoe UI",
    fontWeight: 700,
    fontSize: "10px",
    lineHeight: "13px",
    letterSpacing: 0,
    verticalAlign: "middle"
  },
  budgetStatusActive: {
    backgroundColor: "#1D731C",
    color: "#fff !important"
  },
  budgetStatusUpcoming: {
    backgroundColor: "#FFC328",
    color: "#1E1E1E"
  },
  budgetStatusInactive: {
    backgroundColor: "#D9D9D9 !important",
    color: "#0000004D !important"
  },
  tooltip: {
    width: 220,
    borderRadius: "3px",
    padding: theme.spacing(1, 2),
    boxShadow: "0px 1px 6px 0px #0000004D",
    fontFamily: "Segoe UI",
    fontWeight: 600,
    fontSize: 12,
    lineHeight: "16px",
    letterSpacing: 0,
    backgroundColor: "#fff",
    color: "#1E1E1E"
  },
  tooltipArrow: {
    width: 12,
    height: 6,
    color: "#fff",
    marginBottom: "-6px !important",
    overflow: "hidden",
    borderRadius: 2,
    "&::before": {
      boxShadow: "0px 1px 4px 0px #0000004D"
    }
  },
  servicesValue: {
    fontFamily: "Segoe UI",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: 0,
    color: "#464646",
    width: "100%",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    whiteSpace: "normal",
    wordBreak: "break-word"
  },
  fixedHeightGrid: {
    height: 136,
    minHeight: 136
  },
  servicesRow: {
    marginBottom: 4,
    alignItems: "flex-start",
    minHeight: 32
  },
  servicesValueCell: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%"
  }
}));
interface ServiceProductItemProps {
  serviceProduct: ServiceProduct | ServiceProductWithSolution;
  onClick?: (id: string) => void;
  onDeactivate?: () => void;
  onClose?: () => void;
  showTitle?: boolean;
  detailedView?: boolean;
  notificationReadOnly?: boolean;
  setNotificationReadOnly?: (readOnly: boolean) => void;
  onSave?: (notificationTriggers: NotificationTriggerInput[]) => void;
  loading?: boolean;
  editNotificationErrorMessage?: string;
  onCancel?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const ServiceProductItem: React.FC<ServiceProductItemProps> = ({
  serviceProduct,
  onClick,
  onDeactivate,
  onClose,
  showTitle = true,
  detailedView = false,
  notificationReadOnly = true,
  setNotificationReadOnly,
  onSave,
  loading = false,
  editNotificationErrorMessage,
  onCancel
}) => {
  const formattedTransactionLimit = new Intl.NumberFormat("en-US").format(
    serviceProduct.transactionLimit
  );
  const currentPeriod = `${serviceProduct.currentPeriodNumber || "-"}/${
    serviceProduct.periodCount
  }`;
  const classes = useStyles();

  const notificationSettingRef = useRef<FormikProps<{
    notificationTriggers: NotificationTriggerInput[];
  }> | null>(null);

  const infoRows = (
    <>
      {serviceProduct.apiKey && (
        <Grid container item xs={12}>
          <Grid item xs={6} className={classes.propertyGrid}>
            <Typography className={classes.property}>API Key</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography className={classes.value}>
              {serviceProduct.apiKey.name}
            </Typography>
          </Grid>
        </Grid>
      )}

      {serviceProduct.startDate && serviceProduct.endDate && (
        <Grid container item xs={12}>
          <Grid item xs={6} className={classes.propertyGrid}>
            <Typography className={classes.property}>
              Start - end date
            </Typography>
          </Grid>
          <Grid item xs={6} style={{ minWidth: 250 }}>
            <Typography className={classes.value}>
              <DateRange
                startDate={serviceProduct.startDate}
                endDate={serviceProduct.endDate}
                variant={DateVariant.SHORT_WITH_TIME}
              />
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid container item xs={12}>
        <Grid item xs={6} className={classes.propertyGrid}>
          <Typography className={classes.property}>
            {onDeactivate ? "Periods" : "Current period"}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.value}>
            {onDeactivate ? serviceProduct.periodCount : currentPeriod}
          </Typography>
        </Grid>
      </Grid>

      <Grid container item xs={12}>
        <Grid item xs={6} className={classes.propertyGrid}>
          <Typography className={classes.property}>Limit per period</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography className={classes.value}>
            {formattedTransactionLimit}
          </Typography>
        </Grid>
      </Grid>

      <Grid container item xs={12}>
        <Grid item xs={6} className={classes.propertyGrid}>
          <Typography className={classes.property}>Type of limit</Typography>
        </Grid>
        <Grid item xs={6}>
          <div className={classes.valueWithIcon}>
            <Typography
              className={classes.value}
              style={{ margin: 0, padding: 0 }}
            >
              {serviceProduct.limitType}
            </Typography>
            {detailedView && (
              <CustomTooltip
                title={
                  serviceProduct.limitType === "Hard"
                    ? "When you reach the limit, you no longer have access to the services"
                    : "When you exceed the limit, services operate normally, but additional fees may apply."
                }
                arrow
                placement="top"
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.tooltipArrow
                }}
              >
                <span className={classes.helpIcon}>
                  <img
                    src={helpIcon}
                    alt="Help"
                    width={16}
                    height={16}
                    className={classes.helpIconImg}
                  />
                </span>
              </CustomTooltip>
            )}
          </div>
        </Grid>
      </Grid>

      <Grid
        container
        item
        xs={12}
        className={classes.servicesRow}
        wrap="nowrap"
      >
        <Grid
          item
          className={classes.propertyGrid}
          style={{ width: 112, flex: "0 0 112px" }}
        >
          <Typography className={classes.property}>Services</Typography>
        </Grid>
        <Grid
          item
          className={classes.servicesValueCell}
          style={{ flex: 1, minWidth: 0 }}
        >
          <CustomTooltip
            title={serviceProduct.services.join(", ") || "-"}
            arrow
          >
            <span>
              <Typography className={classes.servicesValue}>
                {serviceProduct.services.join(", ")}
              </Typography>
            </span>
          </CustomTooltip>
        </Grid>
      </Grid>
    </>
  );

  const deactivatableStatuses: ServiceProductStatus[] = [
    ServiceProductStatus.Active,
    ServiceProductStatus.Upcoming
  ];

  function getBadgeClassName(status: ServiceProductStatus) {
    if (status === "Active")
      return `${classes.budgetStatusText} ${classes.budgetStatusActive}`;
    if (status === "Upcoming")
      return `${classes.budgetStatusText} ${classes.budgetStatusUpcoming}`;
    if (status === "Expired" || status === "Deactivated")
      return `${classes.budgetStatusText} ${classes.budgetStatusInactive}`;
    return classes.budgetStatusText;
  }

  return (
    <Card
      variant="outlined"
      className={classes.card}
      style={{
        cursor: onClick ? "pointer" : "default",
        height: !detailedView
          ? "solutionKey" in serviceProduct
            ? 269
            : 246
          : "auto",
        width: !detailedView ? 400 : 667
      }}
      onClick={onClick ? () => onClick(serviceProduct.id) : undefined}
    >
      <CardContent className={classes.cardContent}>
        <div className={classes.flex}>
          {showTitle && (
            <CustomTooltip
              title={serviceProduct.name}
              arrow
              placement="top"
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.tooltipArrow
              }}
            >
              <Typography
                component="div"
                gutterBottom
                className={clsx(classes.label, {
                  [classes.labelDetailed]: detailedView
                })}
                data-testid={`toptop-${serviceProduct.name}`}
              >
                {serviceProduct.name}
              </Typography>
            </CustomTooltip>
          )}
          {serviceProduct.status && (
            <Badge
              label={serviceProduct.status}
              className={getBadgeClassName(serviceProduct.status)}
            />
          )}
        </div>
        {detailedView ? (
          <Section name="Details">
            <Grid container columnSpacing={2} rowSpacing={0.5}>
              {infoRows}
            </Grid>
          </Section>
        ) : (
          <Grid
            container
            columnSpacing={2}
            rowSpacing={0.5}
            className={classes.fixedHeightGrid}
          >
            {infoRows}
          </Grid>
        )}
        {"solutionKey" in serviceProduct && (
          <Grid container item xs={12}>
            <Grid item xs={6} className={classes.propertyGrid}>
              <Typography className={classes.property}>Solution</Typography>
            </Grid>
            <Grid item xs={6}>
              <Link
                to={createRoute(`/solutions/${serviceProduct.solutionKey}`)}
                onClick={e => e.stopPropagation()}
              >
                <Typography className={classes.value}>
                  {serviceProduct.solutionKey}
                </Typography>
              </Link>
            </Grid>
          </Grid>
        )}
        {detailedView && hasNotificationProperties(serviceProduct) && (
          <>
            <Grid item xs={12} style={{ position: "relative" }}>
              <ServiceProductNotificationEdit
                ref={notificationSettingRef}
                notificationTriggers={serviceProduct.notificationTriggers}
                limitType={serviceProduct.limitType}
                isReadOnly={notificationReadOnly}
                emailFieldWidth={277}
                onEditToggle={setNotificationReadOnly}
                onSave={onSave}
                loading={loading}
                errorMessage={editNotificationErrorMessage}
              />
            </Grid>
            <Grid
              item
              xs={12}
              style={{ marginTop: !notificationReadOnly ? 16 : 0 }}
            >
              <ServiceProductNotificationStatus
                data={serviceProduct.notificationDeliveryStatuses || []}
              />
            </Grid>
          </>
        )}
        {detailedView && (
          <Grid item xs={12}>
            <Section name="Periods">
              <PeriodsWithQuota
                periods={serviceProduct.periods}
                transactionLimit={serviceProduct.transactionLimit}
                status={serviceProduct.status}
                currentPeriodNumber={serviceProduct.currentPeriodNumber}
              />
            </Section>
          </Grid>
        )}
        {!detailedView && serviceProduct.currentPeriod && (
          <Grid item xs={12}>
            <DetailedTransactionQuotaBar
              transactionLimit={serviceProduct.transactionLimit}
              usedTransactions={
                serviceProduct.currentPeriod.usedTransactionCount
              }
              status={serviceProduct.status}
            />
          </Grid>
        )}
      </CardContent>
      {detailedView && (
        <CardActions className={classes.cardActions}>
          <ServiceProductNotificationActions
            notificationReadOnly={notificationReadOnly}
            onDeactivate={onDeactivate}
            onClose={onClose}
            onCancel={onCancel}
            serviceProductStatus={serviceProduct.status}
            deactivatableStatuses={deactivatableStatuses}
            notificationSettingRef={notificationSettingRef}
          />
        </CardActions>
      )}
    </Card>
  );
};

export default ServiceProductItem;
